/**
 * Crosstab Preview Component (v2.2 - Phase 2: Interactivity)
 *
 * CrosstabDataModelを受け取り、テーブルとして表示する
 * 複数値フィールドは列として横に展開（Column × Measure Pivot）
 * Phase 2: Selection統合、ドリルダウン機能
 */

import React, { useMemo, useCallback } from 'react';
import type {
  CrosstabDataModel,
  ColumnHeader,
  RowHeader,
  SortConfig,
  SortSetting
} from '../../../../utils/crosstab/types';
import type { EphemeralDataSet, FilterExpression, ComparisonCondition, DataValue } from '../../../../../types';
import { createColumnName } from '../../../../../types';
import { generateCellKey } from '../../../../utils/crosstab/keyGeneration';
import { useDatasetSelection } from '../../../../stores/hooks/useDatasetSelection';
import { useProjectStore } from '../../../../stores/projectStore';
import { createFilteredDataTab } from '../../../../tab-system/TabRegistry';
import { useTooltip } from '../../../../hooks/useTooltip';
import { useProjectData } from '../../../../stores/hooks/useProjectData';
import { createDatasetId } from '../../../../../types';
import { FilterExpressionParser } from '../../../../utils/FilterExpressionParser';
import {
  COLOR_SCHEMES,
  calculateStatistics,
  mapValueToColor,
  getContrastTextColor
} from '../../../../utils/crosstab/colorSchemes';
import * as styles from './CrosstabPreview.css';
import * as parentStyles from '../CrosstabTabContent.css';

interface CrosstabPreviewProps {
  result: CrosstabDataModel;
  isStale?: boolean;
  sortConfig?: SortConfig;
}

export const CrosstabPreview: React.FC<CrosstabPreviewProps> = ({
  result,
  isStale = false,
  sortConfig
}) => {
  const { configuration, structure, data, metadata, cellToRecords } = result;
  const { rowHeaders, columnHeaders } = structure;
  const { cells, rowTotals, columnTotals, grandTotal } = data;
  const { valueFields, options } = configuration;

  // Selection統合
  const { selectRows, selectedRows } = useDatasetSelection(metadata.datasetId);
  const addTab = useProjectStore((state) => state.addTab);
  const getCurrentWorkspace = useProjectStore((state) => state.getCurrentWorkspace);
  const layout = getCurrentWorkspace()?.layoutState || { rootPane: null, activeContainerId: undefined };

  // Dataset操作
  const { getDataset } = useProjectData();

  // Stale警告用ツールチップ
  const staleTooltip = useTooltip();

  // セルホバー用ツールチップ
  const cellTooltip = useTooltip({ offset: { x: 10, y: 10 } });

  // 値フィールドが複数あるか
  const hasMultipleValues = valueFields.length > 1;

  // Visual mode用の統計値を計算（複数値フィールドの場合は無効化）
  const cellStatistics = useMemo(() => {
    if (hasMultipleValues || options.visualMode === 'table') {
      return null;
    }

    // 全セルの値を収集（null/emptyを除外）
    const allValues: number[] = [];
    cells.forEach((cellValue) => {
      if (!cellValue.isEmpty && cellValue.values[0] !== null) {
        allValues.push(cellValue.values[0]);
      }
    });

    if (allValues.length === 0) {
      return null;
    }

    return calculateStatistics(allValues);
  }, [cells, hasMultipleValues, options.visualMode]);

  // Color schemeを取得
  const colorScheme = useMemo(() => {
    const schemeName = options.colorScheme || 'blue';
    return COLOR_SCHEMES[schemeName];
  }, [options.colorScheme]);

  // セルの選択状態を計算
  const getCellSelectionState = useCallback(
    (cellKey: string): { state: 'unselected' | 'partial' | 'full'; selectedCount: number; totalCount: number } => {
      const recordIndices = cellToRecords.get(cellKey) || [];
      const totalCount = recordIndices.length;

      if (totalCount === 0 || selectedRows.length === 0) {
        return { state: 'unselected', selectedCount: 0, totalCount };
      }

      const selectedSet = new Set(selectedRows);
      const selectedCount = recordIndices.filter(idx => selectedSet.has(idx)).length;

      if (selectedCount === 0) {
        return { state: 'unselected', selectedCount: 0, totalCount };
      }

      if (selectedCount === totalCount) {
        return { state: 'full', selectedCount, totalCount };
      }

      return { state: 'partial', selectedCount, totalCount };
    },
    [cellToRecords, selectedRows]
  );

  // ソート処理関数
  const sortHeaders = useCallback(
    (
      headers: RowHeader[] | ColumnHeader[],
      setting: SortSetting | undefined,
      isRowHeaders: boolean
    ): (RowHeader | ColumnHeader)[] => {
      if (!setting) return headers;

      const sorted = [...headers];

      switch (setting.method) {
        case 'label-asc':
          return sorted.sort((a, b) => {
            const aLabel = a.displayValues[0] || '';
            const bLabel = b.displayValues[0] || '';
            return aLabel.localeCompare(bLabel);
          });

        case 'label-desc':
          return sorted.sort((a, b) => {
            const aLabel = a.displayValues[0] || '';
            const bLabel = b.displayValues[0] || '';
            return bLabel.localeCompare(aLabel);
          });

        case 'value': {
          const { sortByKey = 'total', order = 'desc' } = setting;

          return sorted.sort((a, b) => {
            let aValue = 0;
            let bValue = 0;

            if (sortByKey === 'total') {
              // Totalでソート
              const totalsMap = isRowHeaders ? rowTotals : columnTotals;
              aValue = totalsMap?.get(a.key)?.values[0] ?? 0;
              bValue = totalsMap?.get(b.key)?.values[0] ?? 0;
            } else {
              // 特定の列/行でソート
              if (isRowHeaders) {
                // 行をソート: 指定された列の値を使用
                const aCellKey = generateCellKey(a.key, sortByKey);
                const bCellKey = generateCellKey(b.key, sortByKey);
                aValue = cells.get(aCellKey)?.values[0] ?? 0;
                bValue = cells.get(bCellKey)?.values[0] ?? 0;
              } else {
                // 列をソート: 指定された行の値を使用
                const aCellKey = generateCellKey(sortByKey, a.key);
                const bCellKey = generateCellKey(sortByKey, b.key);
                aValue = cells.get(aCellKey)?.values[0] ?? 0;
                bValue = cells.get(bCellKey)?.values[0] ?? 0;
              }
            }

            // 順序に応じて比較
            return order === 'desc' ? bValue - aValue : aValue - bValue;
          });
        }

        default:
          return sorted;
      }
    },
    [cells, rowTotals, columnTotals]
  );

  // ソート済みヘッダー
  const sortedRowHeaders = useMemo(
    () => sortHeaders(rowHeaders, sortConfig?.rows, true) as RowHeader[],
    [rowHeaders, sortConfig?.rows, sortHeaders]
  );

  const sortedColumnHeaders = useMemo(
    () => sortHeaders(columnHeaders, sortConfig?.columns, false) as ColumnHeader[],
    [columnHeaders, sortConfig?.columns, sortHeaders]
  );

  // セルクリックハンドラー: Selectionを更新（トグル動作）
  const handleCellClick = useCallback(
    (cellKey: string) => {
      const recordIndices = cellToRecords.get(cellKey);
      if (!recordIndices || recordIndices.length === 0) return;

      const selectionState = getCellSelectionState(cellKey);

      if (selectionState.state === 'full') {
        // 全選択 → 選択解除（トグル動作）
        selectRows([]);
      } else {
        // 未選択または一部選択 → このセルの全レコードを選択
        selectRows(recordIndices);
      }
    },
    [cellToRecords, selectRows, getCellSelectionState]
  );

  // セルダブルクリックハンドラー: Filtered Dataタブを開く
  const handleCellDoubleClick = useCallback((cellKey: string) => {
    if (!layout.activeContainerId) return;

    // cellKeyから行キーと列キーを抽出: "row:${rowKey}:col:${colKey}"
    const match = cellKey.match(/^row:(.+):col:(.+)$/);
    if (!match) return;

    const rowKey = match[1];
    const colKey = match[2];

    // JSON.parseして値の配列を取得
    const rowValues = JSON.parse(rowKey) as (string | number | null)[];
    const colValues = JSON.parse(colKey) as (string | number | null)[];

    // FilterExpressionを構築
    const conditions: ComparisonCondition[] = [];

    // Row条件を追加
    configuration.rowFields.forEach((field, index) => {
      conditions.push({
        column: createColumnName(field.fieldName),
        operator: '=',
        value: rowValues[index]
      });
    });

    // Column条件を追加
    configuration.columnFields.forEach((field, index) => {
      conditions.push({
        column: createColumnName(field.fieldName),
        operator: '=',
        value: colValues[index]
      });
    });

    // CompoundConditionとして結合
    const filterExpression: FilterExpression = conditions.length === 1
      ? conditions[0]
      : {
          type: 'compound',
          operator: 'and',
          conditions
        };

    // 説明文を生成
    const description = conditions
      .map(c => `${c.column}=${c.value}`)
      .join(' AND ');

    // ソースデータセットを取得
    const sourceDataset = getDataset(metadata.datasetId);
    if (!sourceDataset) return;

    // フィルタ済みデータを生成
    const filteredIndices: number[] = [];
    const rowCount = sourceDataset.rowCount || 0;

    for (let i = 0; i < rowCount; i++) {
      const rowData: Record<string, DataValue> = {};
      sourceDataset.schema.columns.forEach(col => {
        const columnData = sourceDataset.type === 'primary'
          ? sourceDataset.data[col.id]
          : sourceDataset.type === 'derived' && sourceDataset.data
            ? sourceDataset.data[col.id]
            : [];
        rowData[col.name] = columnData[i];
      });

      if (FilterExpressionParser.evaluate(filterExpression, rowData)) {
        filteredIndices.push(i);
      }
    }

    // フィルタ済みデータを構築
    const filteredData: Record<string, DataValue[]> = {};
    sourceDataset.schema.columns.forEach(col => {
      const sourceData = sourceDataset.type === 'primary'
        ? sourceDataset.data[col.id]
        : sourceDataset.type === 'derived' && sourceDataset.data
          ? sourceDataset.data[col.id]
          : [];
      filteredData[col.id] = filteredIndices.map(i => sourceData[i]);
    });

    // EphemeralDataSetを作成
    const ephemeralDataset: EphemeralDataSet = {
      id: createDatasetId(`dataset-ephemeral-${crypto.randomUUID()}`),
      name: description,
      type: 'ephemeral',
      schema: sourceDataset.schema,
      data: filteredData,
      rowCount: filteredIndices.length,
      metadata: {
        sourceDatasetId: metadata.datasetId,
        filterExpression,
        description,
        createdAt: new Date().toISOString(),
        purpose: 'filtered_view'
      }
    };

    // Filtered Dataタブを開く（EphemeralDataSetをmetadataに渡す）
    const filteredDataTab = createFilteredDataTab(ephemeralDataset.id, description, ephemeralDataset);
    addTab(layout.activeContainerId, filteredDataTab);
  }, [configuration, metadata.datasetId, getDataset, addTab, layout.activeContainerId]);

  // セルホバー時のツールチップコンテンツを生成
  const buildCellTooltipContent = (
    rowHeader: { displayValues: string[] },
    colHeader: ColumnHeader,
    cellValue: { values: (number | null)[]; recordCount: number } | undefined,
    cellKey: string
  ) => {
    if (!cellValue) return null;

    // 行×列の識別情報
    const rowInfo = rowHeader.displayValues.join(' / ');
    const colInfo = colHeader.displayValues.join(' / ');

    // 各値フィールドの情報
    const valueLines = valueFields.map((vf, index) => {
      const value = cellValue.values[index];
      const fieldLabel = vf.alias || vf.fieldName;
      const aggLabel = vf.aggregation.toUpperCase();
      return `${fieldLabel} (${aggLabel}): ${formatValue(value)}`;
    });

    // 選択状態
    const selectionState = getCellSelectionState(cellKey);
    const selectionInfo =
      selectionState.state === 'full'
        ? `All ${selectionState.totalCount} records selected`
        : selectionState.state === 'partial'
        ? `${selectionState.selectedCount} of ${selectionState.totalCount} records selected (${Math.round((selectionState.selectedCount / selectionState.totalCount) * 100)}%)`
        : null;

    return (
      <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          {rowInfo} × {colInfo}
        </div>
        <div style={{ marginBottom: '8px' }}>
          {valueLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
        <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
          Based on {cellValue.recordCount} record{cellValue.recordCount !== 1 ? 's' : ''}
        </div>
        {selectionInfo && (
          <div style={{ fontSize: '11px', color: '#1976D2', fontWeight: 600, marginBottom: '4px' }}>
            {selectionInfo}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#888', borderTop: '1px solid #ddd', paddingTop: '4px' }}>
          <div>
            Click to {selectionState.state === 'full' ? 'deselect' : 'select all'}
          </div>
          <div>Double-click to view details</div>
        </div>
      </div>
    );
  };

  // Column値でグループ化（2段階ヘッダー用）
  const columnGroups = useMemo(() => {
    const groups = new Map<string, ColumnHeader[]>();

    for (const header of sortedColumnHeaders) {
      if (!groups.has(header.key)) {
        groups.set(header.key, []);
      }
      groups.get(header.key)!.push(header);
    }

    return Array.from(groups.values());
  }, [sortedColumnHeaders]);

  // 数値フォーマット
  const formatValue = (value: number | null): string => {
    if (value === null || value === undefined) {
      return 'NULL';
    }
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  // パーセンテージフォーマット
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Visual mode用のセルスタイルを計算
  const getCellVisualStyle = (value: number | null): {
    backgroundColor?: string;
    color?: string;
    position?: 'relative';
    barWidth?: number;
  } | null => {
    if (!cellStatistics || value === null || options.visualMode === 'table') {
      return null;
    }

    const { min, max, median } = cellStatistics;
    const bgColor = mapValueToColor(value, min, max, median, colorScheme);

    switch (options.visualMode) {
      case 'heatmap':
        return {
          backgroundColor: bgColor
        };

      case 'text-heatmap':
        return {
          backgroundColor: bgColor,
          color: getContrastTextColor(bgColor)
        };

      case 'bar-in-cell': {
        // バーの幅を計算（0-100%、最大値に対する比率）
        const barWidth = max === 0 ? 0 : (value / max) * 100;
        return {
          position: 'relative',
          barWidth
        };
      }

      default:
        return null;
    }
  };

  // 単一セルの値を表示
  const renderCellValue = (
    value: number | null,
    percentage?: {
      row?: number | undefined;
      column?: number | undefined;
      total?: number | undefined;
    },
    selectionInfo?: { selectedCount: number; totalCount: number },
    visualStyle?: ReturnType<typeof getCellVisualStyle>
  ) => {
    // Heatmap mode: 値を表示しない（背景色のみ）
    if (options.visualMode === 'heatmap') {
      return null;
    }

    // Bar in Cell mode: バーと値を表示
    if (options.visualMode === 'bar-in-cell' && visualStyle?.barWidth !== undefined) {
      return (
        <div className={styles.valueRow} style={{ position: 'relative' }}>
          {/* 背景バー */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${visualStyle.barWidth}%`,
              backgroundColor: colorScheme.colors.max,
              opacity: 0.3,
              zIndex: 0
            }}
          />
          {/* 値（バーの上に表示） */}
          <div className={styles.primaryValue} style={{ position: 'relative', zIndex: 1 }}>
            {formatValue(value)}
          </div>
          {selectionInfo && selectionInfo.selectedCount > 0 && (
            <div className={styles.selectionCount} style={{ position: 'relative', zIndex: 1 }}>
              ({selectionInfo.selectedCount}/{selectionInfo.totalCount})
            </div>
          )}

          {/* パーセンテージ表示 */}
          {percentage && (
            <div style={{ position: 'relative', zIndex: 1 }}>
              {percentage.row !== undefined && (
                <div className={styles.percentageValue}>
                  Row: {formatPercentage(percentage.row)}
                </div>
              )}
              {percentage.column !== undefined && (
                <div className={styles.percentageValue}>
                  Col: {formatPercentage(percentage.column)}
                </div>
              )}
              {percentage.total !== undefined && (
                <div className={styles.percentageValue}>
                  Total: {formatPercentage(percentage.total)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Table mode または Text + Heatmap mode: 通常表示
    return (
      <div className={styles.valueRow}>
        <div className={styles.primaryValue}>{formatValue(value)}</div>
        {selectionInfo && selectionInfo.selectedCount > 0 && (
          <div className={styles.selectionCount}>
            ({selectionInfo.selectedCount}/{selectionInfo.totalCount})
          </div>
        )}

        {/* パーセンテージ表示 */}
        {percentage && (
          <>
            {percentage.row !== undefined && (
              <div className={styles.percentageValue}>
                Row: {formatPercentage(percentage.row)}
              </div>
            )}
            {percentage.column !== undefined && (
              <div className={styles.percentageValue}>
                Col: {formatPercentage(percentage.column)}
              </div>
            )}
            {percentage.total !== undefined && (
              <div className={styles.percentageValue}>
                Total: {formatPercentage(percentage.total)}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
        <thead>
          {/* 第1行: Column値ヘッダー（複数値フィールドの場合はcolspanで結合） */}
          {hasMultipleValues && (
            <tr>
              {/* Corner cell with rowspan */}
              <th className={styles.cornerCell} rowSpan={2}>
                {configuration.rowFields.map(f => f.fieldName).join(', ')}
                {' \\ '}
                {configuration.columnFields.map(f => f.fieldName).join(', ')}
                {isStale && (
                  <span
                    className={parentStyles.staleIndicator}
                    onMouseEnter={(e) =>
                      staleTooltip.show(
                        'Result is outdated. The configuration has changed since this result was generated. Click "Generate Crosstab" to update.',
                        e
                      )
                    }
                    onMouseLeave={staleTooltip.hide}
                  >
                    <span className={parentStyles.staleIcon}>⚠️</span>
                  </span>
                )}
              </th>

              {/* Column value headers with colspan */}
              {columnGroups.map((group, groupIndex) => (
                <th key={`col-group-${groupIndex}`} colSpan={group.length}>
                  {group[0].displayValues.join(' / ')}
                </th>
              ))}

              {/* Row totals header with colspan */}
              {options.showRowTotals && (
                <th className={styles.totalCell} colSpan={valueFields.length}>
                  Total
                </th>
              )}
            </tr>
          )}

          {/* 第2行: Measureヘッダー */}
          <tr>
            {/* Corner cell (単一値フィールドの場合) */}
            {!hasMultipleValues && (
              <th className={styles.cornerCell}>
                {configuration.rowFields.map(f => f.fieldName).join(', ')}
                {' \\ '}
                {configuration.columnFields.map(f => f.fieldName).join(', ')}
                {isStale && (
                  <span
                    className={parentStyles.staleIndicator}
                    onMouseEnter={(e) =>
                      staleTooltip.show(
                        'Result is outdated. The configuration has changed since this result was generated. Click "Generate Crosstab" to update.',
                        e
                      )
                    }
                    onMouseLeave={staleTooltip.hide}
                  >
                    <span className={parentStyles.staleIcon}>⚠️</span>
                  </span>
                )}
              </th>
            )}

            {/* Measure headers */}
            {sortedColumnHeaders.map((colHeader, index) => (
              <th key={`${colHeader.key}-${index}`}>
                {hasMultipleValues
                  ? colHeader.measureFieldAlias || colHeader.measureFieldName
                  : colHeader.displayValues.join(' / ')}
              </th>
            ))}

            {/* Row totals measure headers */}
            {options.showRowTotals && (
              <>
                {hasMultipleValues ? (
                  // 複数値フィールド: 各Measureのヘッダーを表示
                  valueFields.map((vf, index) => (
                    <th key={`total-measure-${index}`} className={styles.totalCell}>
                      {vf.alias || vf.fieldName}
                    </th>
                  ))
                ) : (
                  // 単一値フィールド: "Total"のみ
                  <th className={styles.totalCell}>Total</th>
                )}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {/* Data rows */}
          {sortedRowHeaders.map(rowHeader => (
            <tr key={rowHeader.key}>
              {/* Row header */}
              <th className={styles.rowHeader}>
                {rowHeader.displayValues.join(' / ')}
              </th>

              {/* Data cells */}
              {sortedColumnHeaders.map((colHeader, colIndex) => {
                const cellKey = generateCellKey(rowHeader.key, colHeader.key);
                const cellValue = cells.get(cellKey);

                // measureIndexに基づいて値を取り出す
                const value = cellValue?.values[colHeader.measureIndex] ?? null;
                const percentage = cellValue?.percentages
                  ? {
                      row: cellValue.percentages.row?.[colHeader.measureIndex],
                      column: cellValue.percentages.column?.[colHeader.measureIndex],
                      total: cellValue.percentages.total?.[colHeader.measureIndex]
                    }
                  : undefined;

                // 選択状態を計算
                const selectionState = getCellSelectionState(cellKey);
                const selectionClass =
                  selectionState.state === 'full'
                    ? styles.selectedCellFull
                    : selectionState.state === 'partial'
                    ? styles.selectedCellPartial
                    : '';

                // Visual mode用のスタイルを計算
                const visualStyle = getCellVisualStyle(value);
                const cellStyle: React.CSSProperties = {};
                if (visualStyle) {
                  if (visualStyle.backgroundColor) {
                    cellStyle.backgroundColor = visualStyle.backgroundColor;
                  }
                  if (visualStyle.color) {
                    cellStyle.color = visualStyle.color;
                  }
                }

                return (
                  <td
                    key={`${cellKey}-${colIndex}`}
                    className={`${styles.valueCell} ${styles.clickableCell} ${selectionClass}`}
                    style={cellStyle}
                    onClick={() => handleCellClick(cellKey)}
                    onDoubleClick={() => handleCellDoubleClick(cellKey)}
                    onMouseEnter={(e) => {
                      const tooltipContent = buildCellTooltipContent(rowHeader, colHeader, cellValue, cellKey);
                      if (tooltipContent) {
                        cellTooltip.show(tooltipContent, e);
                      }
                    }}
                    onMouseLeave={cellTooltip.hide}
                  >
                    {cellValue && !cellValue.isEmpty ? (
                      renderCellValue(
                        value,
                        percentage,
                        selectionState.state === 'partial'
                          ? { selectedCount: selectionState.selectedCount, totalCount: selectionState.totalCount }
                          : undefined,
                        visualStyle
                      )
                    ) : (
                      <span className={styles.nullValue}>
                        {options.emptyDisplayMode === 'zero' ? '0' : '—'}
                      </span>
                    )}
                  </td>
                );
              })}

              {/* Row total */}
              {options.showRowTotals && rowTotals && (
                <>
                  {hasMultipleValues ? (
                    // 複数値フィールド: 各Measureごとにセルを表示
                    valueFields.map((_, measureIndex) => {
                      const rowTotalValue = rowTotals.get(rowHeader.key);
                      const value = rowTotalValue?.values[measureIndex] ?? null;

                      return (
                        <td
                          key={`${rowHeader.key}-total-${measureIndex}`}
                          className={`${styles.valueCell} ${styles.totalCell}`}
                        >
                          {rowTotalValue ? (
                            renderCellValue(value)
                          ) : (
                            <span className={styles.nullValue}>—</span>
                          )}
                        </td>
                      );
                    })
                  ) : (
                    // 単一値フィールド: 1つのセル
                    <td className={`${styles.valueCell} ${styles.totalCell}`}>
                      {rowTotals.get(rowHeader.key) && (
                        renderCellValue(rowTotals.get(rowHeader.key)!.values[0])
                      )}
                    </td>
                  )}
                </>
              )}
            </tr>
          ))}

          {/* Column totals row */}
          {options.showColumnTotals && columnTotals && (
            <tr className={styles.totalRow}>
              <th className={styles.rowHeader}>Total</th>

              {sortedColumnHeaders.map((colHeader, colIndex) => {
                const colTotalValue = columnTotals.get(colHeader.key);
                const value = colTotalValue?.values[colHeader.measureIndex] ?? null;

                return (
                  <td
                    key={`total-${colHeader.key}-${colIndex}`}
                    className={`${styles.valueCell} ${styles.totalCell}`}
                  >
                    {colTotalValue ? (
                      renderCellValue(value)
                    ) : (
                      <span className={styles.nullValue}>—</span>
                    )}
                  </td>
                );
              })}

              {/* Grand total */}
              {options.showGrandTotal && grandTotal && (
                <>
                  {hasMultipleValues ? (
                    // 複数値フィールド: 各Measureごとにセルを表示
                    valueFields.map((_, measureIndex) => {
                      const value = grandTotal.values[measureIndex] ?? null;

                      return (
                        <td
                          key={`grand-total-${measureIndex}`}
                          className={`${styles.valueCell} ${styles.grandTotalCell}`}
                        >
                          {renderCellValue(value)}
                        </td>
                      );
                    })
                  ) : (
                    // 単一値フィールド: 1つのセル
                    <td className={`${styles.valueCell} ${styles.grandTotalCell}`}>
                      {renderCellValue(grandTotal.values[0])}
                    </td>
                  )}
                </>
              )}
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {/* Summary */}
      <div className={styles.summary}>
        <span>Rows: {sortedRowHeaders.length}</span>
        <span>Columns: {sortedColumnHeaders.length / valueFields.length}</span>
        <span>Cells: {cells.size}</span>
        <span>Value fields: {valueFields.length}</span>
      </div>

      {/* Stale警告ツールチップ */}
      <staleTooltip.Tooltip />

      {/* セルホバーツールチップ */}
      <cellTooltip.Tooltip />
    </div>
  );
};
